import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import {
    createDevcodeIntegration,
    INTEGRATION_ID as DEVCODE_INTEGRATION_ID,
} from '@endeavour/devcode';
import {
    createInovapayIntegration,
    INTEGRATION_ID as INOVAPAY_INTEGRATION_ID,
} from '@endeavour/inovapay';
import { createNuveiIntegration, INTEGRATION_ID as NUVEI_INTEGRATION_ID } from '@endeavour/nuvei';
import { NuveiEnvironment } from '@endeavour/nuvei/dist/method.interface';
import {
    createPay4FunIntegration,
    INTEGRATION_ID as PAY_4_FUN_INTEGRATION_ID,
} from '@endeavour/pay4fun';
import { multiIntegration } from '@endeavour/payment-integration';
import { useClient } from 'urql';

import { config } from '../../../../config/config';
import { isInApp } from '../../../main/util/is-app';
import { useCurrentCurrency } from '../../../shared/hooks/use-current-currency';
import type { FCWithChildren } from '../../../shared/utils/component.interface';
import type {
    IInovapayMethodType,
    IInovapayProcessPaymentMutation,
    IInovapayProcessPaymentMutationVariables,
} from '../../queries/inovapay.graphql';
import { InovapayProcessPaymentDocument } from '../../queries/inovapay.graphql';
import type {
    INuveiMethodType,
    INuveiProcessPaymentMutation,
    INuveiProcessPaymentMutationVariables,
} from '../../queries/nuvei.graphql';
import { NuveiProcessPaymentDocument } from '../../queries/nuvei.graphql';
import type {
    IPay4funBanksQuery,
    IPay4funBanksQueryVariables,
    IPay4FunMethodType,
    IPay4funProcessPaymentMutation,
    IPay4funProcessPaymentMutationVariables,
} from '../../queries/pay4fun.graphql';
import { Pay4funBanksDocument, Pay4funProcessPaymentDocument } from '../../queries/pay4fun.graphql';
import { PaymentIntegrationProvider } from '../../util/provider-context';

const getRedirectUrl = (type: 'Withdrawal' | 'Deposit', transactionId?: string) => {
    return isInApp()
        ? `${
              config.SPORT_ENDPOINT
          }/app/account/cashier/complete?transactionId={transactionId}&method=${type.toLowerCase()}`
        : `${window.location.origin}/cashier/redirect.html?transactionId=${
              transactionId ?? '{transactionId}'
          }&method=${type === 'Withdrawal' ? 'withdrawal' : 'deposit'}`;
};

const getDeepLink = (transactionId?: string) => {
    return isInApp()
        ? `${config.SPORT_ENDPOINT}/cashier/nuvei/complete/${transactionId ?? '{transactionId}'}`
        : `${window.location.origin}/cashier/nuvei/complete/${transactionId ?? '{transactionId}'}`;
};

export const PaymentsProvider: FCWithChildren<unknown> = ({ children }) => {
    const client = useClient();
    const auth = useAuth();
    const userProfileId = auth.user?.profile.user_profile_id as string;
    const currency = useCurrentCurrency();

    // TODO: This is temporary, payment providers should /not/ be different depending on environment.
    const isDevcodeEnabled = config.TENANT_ID === 'demo1';

    const paymentIntegration = useMemo(
        () =>
            multiIntegration({
                [PAY_4_FUN_INTEGRATION_ID]: createPay4FunIntegration({
                    async processPayment({
                        externalPaymentMethodId,
                        sessionContextId,
                        type,
                        email,
                        amount,
                        bankCode,
                        bankBranch,
                        bankAccount,
                        bankAccountType,
                        pixKeyType,
                    }) {
                        const returnUrl = getRedirectUrl(type);
                        const { data, error } = await client
                            .mutation<
                                IPay4funProcessPaymentMutation,
                                IPay4funProcessPaymentMutationVariables
                            >(Pay4funProcessPaymentDocument, {
                                xTenantID: config.TENANT_ID,
                                userProfileId,
                                type: type as IPay4FunMethodType,
                                payment: {
                                    amount,
                                    externalPaymentMethodId,
                                    sessionContextId,
                                    email,
                                    // TODO: Real language code
                                    languageCode: 'pt',
                                    currencyCode: currency,
                                    successUrl: returnUrl,
                                    errorUrl: returnUrl,
                                    bankCode,
                                    bankBranch,
                                    bankAccount,
                                    bankAccountType,
                                    pixKeyType,
                                },
                            })
                            .toPromise();

                        if (!data || !data.pay4funLaunch) {
                            throw error;
                        }

                        return data.pay4funLaunch;
                    },
                    async getBankList({ sessionContextId }) {
                        const { data, error } = await client
                            .query<IPay4funBanksQuery, IPay4funBanksQueryVariables>(
                                Pay4funBanksDocument,
                                {
                                    xTenantID: config.TENANT_ID,
                                    sessionContextId,
                                },
                            )
                            .toPromise();

                        if (!data || !data.pay4funBanks) {
                            throw error;
                        }

                        return data.pay4funBanks.map(({ id, name }) => ({ id, name: name ?? id }));
                    },
                }),
                [NUVEI_INTEGRATION_ID]: createNuveiIntegration({
                    merchantId: config.CASHIER.nuvei.MERCHANT_ID,
                    merchantSiteId: config.CASHIER.nuvei.SITE_ID,
                    environment:
                        config.CASHIER.nuvei.ENVIRONMENT === 'prod'
                            ? NuveiEnvironment.PRODUCTION
                            : NuveiEnvironment.INTEGRATION,
                    async processPayment({ externalPaymentMethodId, sessionContextId, type }) {
                        const returnUrl = getRedirectUrl(type);
                        const deepLink = getDeepLink();
                        const { data, error } = await client
                            .mutation<
                                INuveiProcessPaymentMutation,
                                INuveiProcessPaymentMutationVariables
                            >(NuveiProcessPaymentDocument, {
                                xTenantID: config.TENANT_ID,
                                userProfileId,
                                type: type as INuveiMethodType,
                                payment: {
                                    externalPaymentMethodId,
                                    sessionContextId,
                                    currencyCode: currency,
                                    successUrl: returnUrl,
                                    errorUrl: returnUrl,
                                    backUrl: returnUrl,
                                    deepLinkUrl: deepLink,
                                },
                            })
                            .toPromise();

                        if (!data || !data.nuveiLaunch) {
                            throw error;
                        }

                        return data.nuveiLaunch;
                    },
                }),
                [INOVAPAY_INTEGRATION_ID]: createInovapayIntegration({
                    async processPayment({
                        externalPaymentMethodId,
                        sessionContextId,
                        type,
                        email,
                        amount,
                        branch,
                        account,
                        accountType,
                    }) {
                        const returnUrl = getRedirectUrl(type);
                        const { data, error } = await client
                            .mutation<
                                IInovapayProcessPaymentMutation,
                                IInovapayProcessPaymentMutationVariables
                            >(InovapayProcessPaymentDocument, {
                                xTenantID: config.TENANT_ID,
                                userProfileId,
                                type: type as IInovapayMethodType,
                                payment: {
                                    amount,
                                    // TODO: get language,
                                    languageCode: 'pt',
                                    externalPaymentMethodId,
                                    sessionContextId,
                                    currencyCode: currency,
                                    email,
                                    returnUrl,
                                    branch,
                                    account,
                                    accountType,
                                },
                            })
                            .toPromise();

                        if (!data || !data.inovapayLaunch) {
                            throw error;
                        }

                        return data.inovapayLaunch;
                    },
                }),
                ...(isDevcodeEnabled
                    ? {
                          [DEVCODE_INTEGRATION_ID]: createDevcodeIntegration({
                              merchantId: config.CASHIER.devcode.MERCHANT_ID,
                              apiUrl: config.CASHIER.devcode.API_ENDPOINT,
                              hostedFieldUrl: config.CASHIER.devcode.HOSTED_FIELD_URL,
                              sessionTimeout: config.CASHIER.devcode.SESSION_TIMEOUT,
                          }),
                      }
                    : {}),
            }),
        [currency, client, userProfileId, isDevcodeEnabled],
    );

    return (
        <PaymentIntegrationProvider value={paymentIntegration}>
            {children}
        </PaymentIntegrationProvider>
    );
};
